import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal, Overline, MaskedLine } from "../components/Reveal";
import { MIST_IMAGE, TREE_IMAGE, HERO_IMAGE } from "../data/modules";

const ADN = [
  "Notre écoute, notre curiosité positive et notre questionnement pertinent.",
  "Notre aptitude à donner du temps au temps, selon votre situation et vos besoins.",
  "Notre réactivité, notre efficacité et notre pragmatisme, au service de vos enjeux.",
  "Notre réseau multi-sectoriel et notre volonté de vous en faire bénéficier.",
  "Notre expérience et notre culture internationales.",
];

const FOUNDERS = [
  {
    initials: "GB", name: "Gilles de Buretel de Chassey", role: "Fondateur",
    linkedin: "https://www.linkedin.com/in/gdechassey",
    bio: "Vingt ans à accompagner dirigeants et cadres dans leurs transitions, prises de fonction et transformations. Gilles porte la vision VAV : rendre l'excellence de l'accompagnement accessible à chaque échelon de la vie professionnelle.",
  },
  {
    initials: "AP", name: "Auriane de Potesta", role: "Co-fondatrice",
    linkedin: null,
    bio: "Coach de dirigeants reconnue pour son écoute, son questionnement et son art de mettre en relation. Auriane incarne la vertu agissante du cabinet : une exigence éthique au service de trajectoires durables.",
  },
];

const VALEURS = [
  { title: "Engagement", text: "Au service de vos enjeux, avec réactivité et constance." },
  { title: "Authenticité", text: "Travailler en partenariat, en confiance." },
  { title: "Audace", text: "Oser nommer, oser bouger, oser choisir." },
  { title: "Humilité", text: "Respecter votre intelligence du terrain." },
];

const ETAPES = [
  { n: "01", title: "Les vingt premières années", text: "Plus de vingt ans au service des cadres dirigeants : executive coaching, outplacement, prises de fonction. Des centaines de trajectoires accompagnées, un réseau actif, une exigence reconnue." },
  { n: "02", title: "La naissance de VAV", text: "De ce constat est né VAV : pourquoi réserver l'accompagnement aux seuls dirigeants ? Le premier cabinet à proposer une véritable assurance vie professionnelle à tous, du cadet au président." },
  { n: "03", title: "Demain : la vraie vie", text: "Notre ambition : accompagner chaque personne jusqu'à l'épanouissement de sa plus grande maturité — et faire de la retraite un sommet, pas une sortie." },
];

export default function Cabinet() {
  return (
    <div data-testid="cabinet-page" className="bg-paper">
      <Navbar />
      <main className="pt-[72px]">
        <section className="relative min-h-[70vh] flex items-end overflow-hidden">
          <img src={MIST_IMAGE} alt="Forêt dans la brume" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-paper via-white/30 to-white/10" />
          <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-20 pt-40 w-full">
            <MaskedLine delay={0.15}><Overline>Le cabinet</Overline></MaskedLine>
            <h1 className="mt-6 font-serif text-5xl md:text-7xl tracking-tighter leading-[0.95] text-ink">
              <MaskedLine delay={0.3}>Des orfèvres de</MaskedLine>
              <MaskedLine delay={0.45}><em className="italic text-sagedark">l'accompagnement</em>.</MaskedLine>
            </h1>
          </div>
        </section>

        <section className="py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14 items-start">
            <Reveal>
              <Overline>Notre histoire</Overline>
              <p className="mt-8 font-serif text-2xl md:text-4xl leading-snug text-ink">
                Nous nous définissons comme des orfèvres de l'accompagnement. Plus de vingt ans au service
                des cadres dirigeants, un ancrage solide, un réseau actif.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="space-y-5">
                {FOUNDERS.map((f) => (
                  <div key={f.initials} data-testid={`founder-card-${f.initials.toLowerCase()}`} className="rounded-3xl bg-white p-8 md:p-10">
                    <div className="flex items-center gap-5">
                      <span className="w-16 h-16 rounded-full bg-ink text-paper font-serif italic text-2xl flex items-center justify-center shrink-0">{f.initials}</span>
                      <div>
                        <p className="font-serif text-xl text-ink">{f.name}</p>
                        <p className="text-xs uppercase tracking-[0.2em] text-smoke mt-1">{f.role}</p>
                      </div>
                      {f.linkedin && (
                        <a
                          data-testid="founder-linkedin-link"
                          href={f.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-auto text-xs rounded-full border border-ink/25 px-4 py-2 text-ink hover:bg-ink hover:text-paper transition-colors duration-300"
                        >
                          LinkedIn
                        </a>
                      )}
                    </div>
                    <p className="mt-6 text-sm md:text-base leading-relaxed text-smoke">{f.bio}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-24 md:py-32 bg-bone">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <Reveal><Overline>Le chemin</Overline>
              <h2 className="mt-6 font-serif text-4xl md:text-6xl tracking-tight text-ink">Trois temps, une trajectoire.</h2>
            </Reveal>
            <div className="mt-16 grid md:grid-cols-3 gap-8">
              {ETAPES.map((e, i) => (
                <Reveal key={e.n} delay={0.08 * i}>
                  <div data-testid={`cabinet-step-${i}`} className="rounded-3xl bg-white p-8 md:p-10 h-full">
                    <span className="font-serif text-5xl text-ink/10">{e.n}</span>
                    <h3 className="mt-6 font-serif text-2xl md:text-3xl tracking-tight text-ink">{e.title}</h3>
                    <p className="mt-4 text-sm md:text-base leading-relaxed text-smoke">{e.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14">
            <div>
              <Reveal><Overline>Notre ADN</Overline></Reveal>
              <div className="mt-10 space-y-6">
                {ADN.map((a, i) => (
                  <Reveal key={i} delay={0.05 * i}>
                    <p data-testid={`cabinet-adn-${i}`} className="flex gap-4 text-base md:text-lg text-smoke leading-relaxed">
                      <span className="font-serif text-sagedark shrink-0">{String(i + 1).padStart(2, "0")}</span>{a}
                    </p>
                  </Reveal>
                ))}
              </div>
            </div>
            <div>
              <Reveal><Overline>Nos valeurs</Overline></Reveal>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
                {VALEURS.map((v, i) => (
                  <Reveal key={v.title} delay={0.06 * i}>
                    <div data-testid={`cabinet-valeur-${i}`} className="rounded-3xl bg-bone p-7 h-full">
                      <h3 className="font-serif text-2xl text-ink">{v.title}</h3>
                      <p className="mt-3 text-sm text-smoke leading-relaxed">{v.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden">
          <img src={TREE_IMAGE} alt="Arbre solitaire dans l'eau" className="w-full h-[420px] md:h-[540px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-ink/10 to-transparent" />
          <div className="absolute bottom-10 left-6 md:left-10 max-w-2xl">
            <p className="font-serif text-2xl md:text-4xl text-paper leading-snug">« Sans Vertu, l'Audace d'une entreprise ne peut développer la Vie. »</p>
            <Link data-testid="cabinet-cta-simulateur" to="/simulateur" className="mt-8 inline-flex rounded-full bg-paper text-ink px-8 py-4 text-sm hover:bg-bone transition-colors duration-300">
              Commencer mon parcours
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
